import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { Alert } from '../components/Alert';
import { AuthLayout } from '../components/AuthLayout';
import { Button } from '../components/Button';
import { Field } from '../components/Field';
import { api } from '../lib/api';
import { safeNext, useAuth } from '../lib/auth';
import { messageForError } from '../lib/errors';
import { loginFormSchema, parseForm, type FieldErrors } from '../lib/forms';
import { describeDevice, loginWithPasskey, passkeysSupported } from '../lib/passkeys';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = useAuth();
  const [errors, setErrors] = useState<FieldErrors<'email' | 'password'>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'password' | 'passkey' | null>(null);
  const [email, setEmail] = useState('');

  async function finish() {
    const me = await refresh();
    void navigate(me?.household ? safeNext(location.search) : '/onboarding', { replace: true });
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const r = parseForm(loginFormSchema, e.currentTarget);
    if (r.errors) return setErrors(r.errors as FieldErrors<'email' | 'password'>);
    setErrors({});
    setError(null);
    setBusy('password');
    try {
      await api.auth.login({ ...r.data, deviceName: describeDevice() });
      await finish();
    } catch (err) {
      setError(messageForError(err));
    } finally {
      setBusy(null);
    }
  }

  async function onPasskey() {
    setError(null);
    setBusy('passkey');
    try {
      await loginWithPasskey(email.trim() || undefined);
      await finish();
    } catch (err) {
      setError(messageForError(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <AuthLayout title="FewerBags" subtitle="Plan meals, cook them together, shop with fewer bags.">
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        {error ? <Alert tone="error">{error}</Alert> : null}
        <Field
          id="email"
          name="email"
          label="Email"
          type="email"
          autoComplete="username webauthn"
          inputMode="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={errors.email}
        />
        <Field
          id="password"
          name="password"
          label="Password"
          type="password"
          autoComplete="current-password"
          required
          error={errors.password}
        />
        <Button type="submit" disabled={busy !== null}>
          {busy === 'password' ? 'Signing in…' : 'Sign in'}
        </Button>
        {passkeysSupported() ? (
          <Button type="button" variant="secondary" onClick={onPasskey} disabled={busy !== null}>
            {busy === 'passkey' ? 'Waiting for your passkey…' : 'Sign in with a passkey'}
          </Button>
        ) : null}
        <Link
          to="/forgot-password"
          className="text-center text-sm text-muted underline-offset-2 hover:underline"
        >
          Forgot your password?
        </Link>
      </form>
      <p className="text-center text-sm text-muted">
        New here?{' '}
        <Link to="/register" className="font-medium text-brand underline-offset-2 hover:underline">
          Create an account
        </Link>
      </p>
    </AuthLayout>
  );
}
